/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import personFields from "../modules/personFields";
import * as stagesCase from "../../../Stages/StagesCase";
import { overwriteFormElementField, trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { abstractLitigationStageFormDefinition } from "../abstractLitigationCase/stageFormDefinition";
import { TrafficAccident } from "../../../../types/Products/TrafficLaw/TrafficAccident/TrafficAccident";
import { UseForm } from "../../../../types/UseForm";
import { formValue } from "../../../formServiceFunctions";

export const stageFormDefinition = (product: TrafficAccident): ReturnType<FormDefinition<TrafficAccident>> => {
  const createdFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...personFields({ path: `${product.productClassName}.client` }),
  ];

  const basicTrafficAccidentFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.typeOfDamage`,
      isDisabled: () => true,
      options: () => [{ value: "vehicle" }, { value: "person" }, { value: "vehicleAndPerson" }, { value: "other" }],
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.typeOfAccident`,
      isDisabled: () => true,
      options: () => [
        { value: "rearEndCollision" },
        { value: "laneChangeCollision" },
        { value: "parking" },
        { value: "other" },
      ],
    },
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.courseOfEvents`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
  ];

  const dataCompletedOverwriteFields: FormElementPropsWithoutProduct<TrafficAccident>[] = overwriteFormElementField(
    overwriteFormElementField(
      [...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED]],
      `${product.productClassName}.valueInDisputeExtrajudicialPreliminary`,
      {
        isHidden: ({ values }: { values: UseForm["values"] }) =>
          formValue(values, "trafficAccident.caseCategoryAssessment") === "reject",
      }
    ),
    `${product.productClassName}.extrajudicialCommunicationDeadline`,
    {
      isHidden: ({ values }: { values: UseForm["values"] }) =>
        formValue(values, "trafficAccident.caseCategoryAssessment") === "reject",
    }
  );

  const dataCompletedFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...basicTrafficAccidentFields,
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courseOfEventsLawFirm`,
      isHidden: ({ values }) => formValue(values, "trafficAccident.caseCategoryAssessment") === "reject",
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "SelectAndExtendedDataComboField",
      path: `${product.productClassName}.claimsForDamages`,
      isHidden: ({ values }) => formValue(values, "trafficAccident.caseCategoryAssessment") === "reject",
      additionalProps: {
        fullWidth: true,
        config: {
          values: [
            "repairCosts",
            "replacementCosts",
            "lossOfUse",
            "rentalCarCosts",
            "depreciation",
            "expertFees",
            "expensesFlatFee",
            "deregistrationCosts",
            "registrationCosts",
            "towingCosts",
            "storageCosts",
            "financingCosts",
          ],
          showOther: true,
          extensionFieldConfig: {
            type: "money",
            labelPrefix: "Höhe Schadensersatz",
            md: 12,
          },
        },
      },
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.claimedAmountLawFirm`,
      fieldType: "money",
      numberType: "float",
      isDisabled: () => true,
    },
    ...dataCompletedOverwriteFields,
  ];

  const dataLawFirmCompletedFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [...dataCompletedFields];

  const checkedFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...basicTrafficAccidentFields,
    ...overwriteFormElementField(
      [...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_CHECKED]],
      `${product.productClassName}.extrajudicialCommunicationDeadline`,
      {
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      }
    ),
  ];

  const inProgressFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_IN_PROGRESS],
  ];

  const communicationSendFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.legalExpenseInsurance`,
      options: () => trueFalseDefaultOptions("trafficAccident.legalExpenseInsurance"),
    },
    ...overwriteFormElementField(
      [...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]],
      `${product.productClassName}.extrajudicialCommunicationDeadline`,
      {
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      }
    ),
  ];

  const reactionReceivedFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...overwriteFormElementField(
      [...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]],
      `${product.productClassName}.extrajudicialCommunicationDeadline`,
      {
        isDisabled: ({ product }: { product: TrafficAccident }) =>
          !product.stage ||
          product.stage < stagesCase.STAGE_DATA_COMPLETED ||
          product.stage > stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      }
    ),
  ];

  const actionRequiredFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_ACTION_REQUIRED],
  ];

  const prepareLawsuitFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_PREPARE_LAWSUIT],
  ];

  const lawsuitFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT],
  ];

  const lawsuitCompletedFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT_COMPLETED],
  ];

  const completedFields: FormElementPropsWithoutProduct<TrafficAccident>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_COMPLETED],
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields,
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields,
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields,
    [stagesCase.STAGE_CHECKED]: checkedFields,
    [stagesCase.STAGE_IN_PROGRESS]: inProgressFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: communicationSendFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: reactionReceivedFields,
    [stagesCase.STAGE_ACTION_REQUIRED]: actionRequiredFields,
    [stagesCase.STAGE_PREPARE_LAWSUIT]: prepareLawsuitFields,
    [stagesCase.STAGE_LAWSUIT]: lawsuitFields,
    [stagesCase.STAGE_TRIAL]: lawsuitFields,
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]:
      abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EVALUATE_JUDGEMENT],
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: lawsuitCompletedFields,
    [stagesCase.STAGE_COMPLETED]: completedFields,
  };
};
